import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { Button } from './ui/Button';

export const CookieBanner: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if user has already made a cookie choice
        const cookieConsent = localStorage.getItem('cookieConsent');
        if (!cookieConsent) {
            // Show banner after a short delay to not overwhelm users
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const acceptCookies = () => {
        localStorage.setItem('cookieConsent', 'accepted');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        setIsVisible(false);

        // Enable analytics and other non-essential cookies
        enableAnalytics();
    };

    const rejectCookies = () => {
        localStorage.setItem('cookieConsent', 'rejected');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        setIsVisible(false);

        // Disable analytics and clean up non-essential cookies
        disableAnalytics();
        cleanupNonEssentialCookies();
    };

    const enableAnalytics = () => {
        // Enable your analytics tracking here
        // For example, if using Google Analytics:
        // window.gtag && window.gtag('consent', 'update', { analytics_storage: 'granted' });

        // Or set a flag for your custom analytics
        localStorage.setItem('analyticsEnabled', 'true');
    };

    const disableAnalytics = () => {
        // Disable analytics tracking
        // window.gtag && window.gtag('consent', 'update', { analytics_storage: 'denied' });

        localStorage.setItem('analyticsEnabled', 'false');
    };

    const cleanupNonEssentialCookies = () => {
        // Clean up any non-essential cookies
        // This depends on what cookies your site uses
        const cookiesToRemove = [
            '_ga', '_gid', '_gat', // Google Analytics cookies
            '_fbp', '_fbc',         // Facebook Pixel cookies
            // Add other non-essential cookie names as needed
        ];

        cookiesToRemove.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
        });
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50 border-t border-gray-700">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                    <Cookie className="h-6 w-6 text-primary-400 flex-shrink-0" />
                    <div className="text-sm">
                        <p className="mb-1">
                            We use essential cookies to provide our services and optional cookies to analyze website performance
                            and improve your experience.
                        </p>
                        <p className="text-gray-300">
                            <a
                                href="/privacy"
                                className="text-primary-400 hover:text-primary-300 underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Privacy Policy
                            </a>
                            {' · '}
                            <a
                                href="/cookies"
                                className="text-primary-400 hover:text-primary-300 underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Cookie Policy
                            </a>
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={rejectCookies}
                        className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                    >
                        Essential Only
                    </Button>
                    <Button
                        size="sm"
                        onClick={acceptCookies}
                        className="bg-primary-600 hover:bg-primary-700 text-white"
                    >
                        Accept All
                    </Button>
                    <button
                        onClick={rejectCookies}
                        className="text-gray-400 hover:text-white ml-2 p-1"
                        aria-label="Close and reject optional cookies"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};